import { useEffect, useState } from 'react';
import { Modal, Form, Input, Switch, Button, Select, Typography } from 'antd';
import { X } from 'lucide-react';
import LogoUploader from '@/components/form/LogoUploader';
import {
  TENANT_TIPO_CRIACAO_OPTIONS,
  TENANT_TIPO_UNIDADE_LABELS,
  type TenantTipoUnidade,
} from '@/features/superadmin/utils/tenantRoleResolver';

const { Text } = Typography;

interface TenantFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: {
    id?: string;
    nome: string;
    status: string;
    tipo_unidade?: TenantTipoUnidade | null;
    logo_url?: string | null;
  };
  title: string;
  description: string;
  submitLabel: string;
}

export function TenantForm({
  open,
  onClose,
  onSubmit,
  initialData,
  title,
  description,
  submitLabel
}: TenantFormProps) {
  const [form] = Form.useForm();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset/Initialize form when modal opens or initialData changes
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          nome: initialData.nome,
          ativo: initialData.status === 'ATIVO',
          tipo_unidade: initialData.tipo_unidade ?? undefined,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          ativo: true,
          tipo_unidade: 'ASSOCIADO',
        });
      }
      setLogoFile(null); // Reset logo file state
      setIsSubmitting(false);
    }
  }, [initialData, open, form]);

  const handleFinish = async (values: any) => {
    setIsSubmitting(true);
    const payload: any = {
      nome: values.nome.trim(),
      ativo: values.ativo,
    };

    if (!initialData?.id && values.tipo_unidade) {
      payload.tipo_unidade = values.tipo_unidade;
    }

    if (logoFile) {
      payload.logoFile = logoFile;
    }

    if (initialData?.id) {
      payload.id = initialData.id;
    }

    try {
      await onSubmit(payload);
    } catch (error) {
      console.error('Erro ao submeter formulário:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoSelect = async (file: File) => {
    setLogoFile(file);
    return true; // Return true to indicate successful faux-upload for the preview component
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!isSubmitting) onClose();
      }}
      footer={null}
      closable={false}
      width={600}
      destroyOnHidden
      centered
      styles={{
        mask: { background: 'rgba(0, 0, 0, 0.45)' },
        body: { padding: 0, overflow: 'hidden' },
      }}
      zIndex={1000}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-gray-400 transition"
        aria-label="Fechar"
        disabled={isSubmitting}
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex flex-col h-auto bg-white dark:bg-zinc-900">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          autoComplete="off"
          disabled={isSubmitting}
        >
          <div className="bg-[#0d0d0f] dark:bg-zinc-800 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between gap-4 pr-12">
            <div className="min-w-0">
              <p className="text-[11px] text-white/50 uppercase tracking-wide mb-1">
                {initialData?.id ? 'Editar' : 'Criar'} Empresa
              </p>
              <h2 className="text-[15px] font-semibold">
                {title}
              </h2>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-white/80 whitespace-nowrap">Status</span>
              <Form.Item name="ativo" valuePropName="checked" initialValue={true} className="mb-0">
                <Switch size="small" className="[&_.ant-switch-checked]:bg-[#FF3737]" />
              </Form.Item>
            </div>
          </div>

          <div className="px-6 py-6 flex-1 overflow-y-auto max-h-[70vh]">
            <div style={{ marginBottom: 24 }}>
              <Text type="secondary" className="text-sm">{description}</Text>
            </div>

            <div style={{ marginBottom: 24 }}>
              <LogoUploader
                currentLogoUrl={initialData?.logo_url}
                onLogoUpload={handleLogoSelect}
                isUploading={isSubmitting}
              />
            </div>

            <Form.Item
              name="nome"
              label="Nome da Empresa"
              rules={[{ required: true, message: 'Por favor, insira o nome da empresa' }]}
            >
              <Input placeholder="Ex: Antigravity Solutions" disabled={isSubmitting} />
            </Form.Item>

            {!initialData?.id && (
              <Form.Item
                name="tipo_unidade"
                label="Tipo de Empresa"
                rules={[{ required: true, message: 'Selecione o tipo da empresa' }]}
                extra="Associado (MEI): empresa própria vinculada à matriz — o pós-venda da matriz opera as vendas. Representação: franquia com time isolado (própria secretaria e pós-venda)."
              >
                <Select
                  disabled={isSubmitting}
                  options={TENANT_TIPO_CRIACAO_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                />
              </Form.Item>
            )}

            {initialData?.tipo_unidade && (
              <Form.Item label="Tipo de Empresa">
                <div className="px-3 py-2 bg-[#f5f6f8] dark:bg-zinc-800 rounded-lg text-sm font-medium text-gray-900 dark:text-white">
                  {TENANT_TIPO_UNIDADE_LABELS[initialData.tipo_unidade] ?? initialData.tipo_unidade}
                </div>
              </Form.Item>
            )}

            <div className="flex gap-3 mt-8 pt-4 border-t border-gray-200 dark:border-zinc-700">
              <Button onClick={onClose} style={{ flex: 1 }} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="primary"
                loading={isSubmitting}
                onClick={() => form.submit()}
                style={{ flex: 1, backgroundColor: '#FF3737' }}
              >
                {isSubmitting ? 'Salvando...' : submitLabel}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
}